/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { Banknote, CheckCircle2, Loader2, Receipt } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { IconBadge } from '@/components/ui/icon-badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { TitledCard } from '@/components/ui/titled-card'

import { getBankAccounts, submitDepositClaim } from '../api'
import type { BankAccount } from '../types'

type ClaimResultStatus = 'success' | 'not_found' | 'pending_review' | 'error'

interface ClaimResult {
  status: ClaimResultStatus
  message: string
}

export function BankDepositClaimCard() {
  const { t } = useTranslation()

  const [accounts, setAccounts] = useState<BankAccount[]>([])
  const [loadingAccounts, setLoadingAccounts] = useState(true)

  const [amountToman, setAmountToman] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [bankDate, setBankDate] = useState('')
  const [transferTime, setTransferTime] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<ClaimResult | null>(null)

  useEffect(() => {
    let cancelled = false
    getBankAccounts()
      .then((data) => {
        if (cancelled) return
        setAccounts(data)
        if (data.length > 0) {
          setAccountNumber(data[0].account_number)
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingAccounts(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const canSubmit =
    amountToman.trim() !== '' &&
    accountNumber.trim() !== '' &&
    /^\d{4}$/.test(bankDate.trim()) &&
    /^\d{2}:\d{2}$/.test(transferTime.trim()) &&
    !submitting

  async function handleSubmit() {
    setSubmitting(true)
    setResult(null)
    try {
      const res = await submitDepositClaim({
        amount_toman: Number(amountToman.replace(/,/g, '')),
        account_number: accountNumber,
        bank_date: bankDate.trim(),
        transfer_time: transferTime.trim(),
      })
      setResult({ status: res.status as ClaimResultStatus, message: res.message })
      if (res.status === 'success') {
        setAmountToman('')
        setBankDate('')
        setTransferTime('')
      }
    } catch (err) {
      setResult({
        status: 'error',
        message: err instanceof Error ? err.message : t('Something went wrong'),
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <TitledCard
      title={t('Bank Transfer Top-up')}
      icon={
        <IconBadge>
          <Banknote className='h-4 w-4' />
        </IconBadge>
      }
    >
      <Card>
        <CardHeader>
          {loadingAccounts ? (
            <Skeleton className='h-16 w-full' />
          ) : accounts.length > 0 ? (
            <Alert>
              <Receipt className='h-4 w-4' />
              <AlertDescription>
                {t(
                  'Transfer your desired amount to the account below, then enter the exact amount, date and time shown on your own transfer receipt.'
                )}
                <div className='mt-2 space-y-1 font-medium'>
                  {accounts.map((acc) => (
                    <div key={acc.account_number}>
                      <div>
                        {acc.bank_name}
                        {acc.card_number ? ` — ${acc.card_number}` : ''}
                        {acc.owner_name ? ` (${acc.owner_name})` : ''}
                      </div>
                      {acc.description && (
                        <div className='text-muted-foreground text-sm font-normal'>
                          {acc.description}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </AlertDescription>
            </Alert>
          ) : (
            <Alert variant='destructive'>
              <AlertDescription>
                {t('No deposit account is configured yet. Please contact the administrator.')}
              </AlertDescription>
            </Alert>
          )}
        </CardHeader>

        <CardContent className='space-y-4'>
          <div className='grid gap-4 sm:grid-cols-2'>
            <div className='space-y-2'>
              <Label htmlFor='deposit-amount'>{t('Amount you transferred (Toman)')}</Label>
              <Input
                id='deposit-amount'
                inputMode='numeric'
                placeholder='2000000'
                value={amountToman}
                onChange={(e) => setAmountToman(e.target.value)}
              />
            </div>

            {accounts.length > 1 && (
              <div className='space-y-2'>
                <Label htmlFor='deposit-account'>{t('Destination account')}</Label>
                <select
                  id='deposit-account'
                  className='border-input bg-background h-9 w-full rounded-md border px-3 text-sm'
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                >
                  {accounts.map((acc) => (
                    <option key={acc.account_number} value={acc.account_number}>
                      {acc.bank_name} — {acc.account_number}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className='space-y-2'>
              <Label htmlFor='deposit-date'>{t('Date from your receipt (e.g. 0629)')}</Label>
              <Input
                id='deposit-date'
                inputMode='numeric'
                placeholder='0629'
                maxLength={4}
                value={bankDate}
                onChange={(e) => setBankDate(e.target.value.replace(/\D/g, '').slice(0, 4))}
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='deposit-time'>{t('Time from your receipt (HH:MM)')}</Label>
              <Input
                id='deposit-time'
                placeholder='19:58'
                maxLength={5}
                value={transferTime}
                onChange={(e) => setTransferTime(e.target.value)}
              />
            </div>
          </div>

          {result && (
            <Alert
              variant={result.status === 'success' ? 'default' : 'destructive'}
              className={
                result.status === 'success'
                  ? 'border-green-500/50 bg-green-500/10 text-green-700 dark:text-green-400'
                  : undefined
              }
            >
              {result.status === 'success' && <CheckCircle2 className='h-4 w-4 text-green-600 dark:text-green-400' />}
              <AlertDescription>{result.message}</AlertDescription>
            </Alert>
          )}

          <Button onClick={handleSubmit} disabled={!canSubmit} className='w-full'>
            {submitting ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              t('Verify and credit my wallet')
            )}
          </Button>
        </CardContent>
      </Card>
    </TitledCard>
  )
}
